import {
  type ComponentType,
  type LazyExoticComponent,
  type ReactNode,
  lazy,
  memo,
  Suspense,
} from 'react';

import ErrorBoundary from '@/components/ErrorBoundary';
import { AppLoading } from '@/components/ui/AppLoading';

// Improved error boundary message for development
const DEVerror_MESSAGE = 'An error occurred while loading this component.';

// Types for enhanced lazy loading
interface LazyOptions {
  /** Enable error boundary for this lazy component */
  errorBoundary?: boolean;
  /** Fallback component to show while loading */
  fallback?: ReactNode;
  /** Development-only error message */
  errorMessage?: string;
  /** Preload the component */
  preload?: boolean;
}

/**
 * Enhanced lazy loading utility with performance optimizations
 * @param factory Function that imports the component
 * @param options Configuration options
 * @returns Lazy loaded component with error handling
 */
export function lazyLoad<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  options: LazyOptions = {},
): LazyExoticComponent<T> {
  const {
    errorBoundary = false,
    fallback = <AppLoading />,
    errorMessage = DEVerror_MESSAGE,
    preload = false,
  } = options;

  // Create lazy component
  const LazyComponent = lazy(factory);

  // Optionally preload the component
  if (preload) {
    void factory();
  }

  // Create the inner component with memoization for better performance
  const InnerComponent = memo((props: any) => {
    const content = (
      <Suspense fallback={fallback}>
        <LazyComponent {...props} />
      </Suspense>
    );

    if (errorBoundary) {
      return (
        <ErrorBoundary
          fallback={<div className="p-4 text-destructive">{errorMessage}</div>}
        >
          {content}
        </ErrorBoundary>
      );
    }

    return content;
  });

  // Preserve displayName for debugging
  const lazyDisplayName =
    'displayName' in LazyComponent ? LazyComponent.displayName : 'Component';

  InnerComponent.displayName = `LazyLoaded(${lazyDisplayName})`;

  return InnerComponent as unknown as LazyExoticComponent<T>;
}

/**
 * Enhanced lazy loading for route components with optimizations for route transitions
 * @param factory Function that imports the component
 * @returns Component with built-in suspense and error boundary
 */
export function lazyRouteComponent<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): T {
  const LazyComponent = lazy(factory);

  // Prefetch implementation for route components
  const prefetch = () => {
    void factory();
  };

  // Memoize the component to prevent unnecessary re-renders
  const Component = memo((props: any) => (
    <Suspense fallback={<AppLoading />}>
      <ErrorBoundary>
        <LazyComponent {...props} />
      </ErrorBoundary>
    </Suspense>
  ));

  // Preserve displayName for debugging
  const lazyDisplayName =
    'displayName' in LazyComponent ? LazyComponent.displayName : 'Component';

  Component.displayName = `LazyRoute(${lazyDisplayName})`;

  // Add prefetch method to the component
  (Component as any).prefetch = prefetch;

  return Component as unknown as T;
}

/**
 * Preload a component without rendering it
 * @param factory Function that imports the component
 */
export function preloadComponent(
  factory: () => Promise<{ default: any }>,
): void {
  void factory();
}
